<template>
  <div class="flex w-full h-screen rounded-lg shadow-lg">
    <!-- Left Image & Text -->
    <div
        :style="{ backgroundImage: `url(${require('@/assets/img/Malhadinha-Nova-3.webp')})` }"
        class="w-2/4 hidden md:flex flex-col flex-[1.2] bg-cover bg-center bg-no-repeat relative text-primary px-10"
    >
      <div class="mt-20 text-center">
        <h2 class="text-4xl font-bold mb-2">A new home starts with</h2>
        <h1 class="text-6xl font-bold">Bison Account</h1>
      </div>
    </div>

    <!-- Right Login Form -->
    <div class="w-2/4 flex flex-col flex-1 justify-center bg-white px-12 text-[#4D2F00] relative">
      <!-- Logo -->
      <div class="flex items-center justify-center mb-10">
        <img
            alt="Bison Mobile Logo"
            class="w-20 h-20 mr-3"
            src="@/assets/img/Bison-bank-logo.png"
        />
        <span class="font-semibold text-3xl text-gray-500">Bison Mobile</span>
      </div>

      <!-- Login Form -->
      <form v-if="!showOtpBox" class="flex flex-col" @submit.prevent="handleSubmit">
        <label class="mb-1 text-sm" for="user">Email</label>
        <input
            v-model="user"
            class="w-full px-4 py-3 mb-5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Email"
            type="email"
            id="user"
        />

        <div class="relative mb-5">
          <input
              v-model="currentPassword"
              :type="showCurrentPassword ? 'text' : 'password'"
              placeholder="Password"
              class="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              id="password"
          />
          <button
              @click.prevent="showCurrentPassword = !showCurrentPassword"
              type="button"
              class="absolute inset-y-0 right-4 flex items-center text-gray-400 hover:text-gray-600"
          >
            <component :is="showCurrentPassword ? EyeOff : Eye" class="w-5 h-5"/>
          </button>
        </div>

        <button
            class="bg-primary text-white font-semibold py-3 rounded-md transition-colors flex justify-center items-center disabled:opacity-50"
            type="submit"
            :disabled="loading"
        >
          <span v-if="!loading">LOGIN</span>
          <svg v-else class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
          </svg>
        </button>

        <!-- پیام خطا/موفقیت -->
        <p v-if="errorMessage" class="mt-3 text-red-600 text-sm">{{ errorMessage }}</p>
        <p v-if="successMessage" class="mt-3 text-green-600 text-sm">{{ successMessage }}</p>
      </form>

      <!-- OTP Form -->
      <form v-if="showOtpBox" class="flex flex-col" @submit.prevent="handleOtpSubmit">

        <p class="text-gray-500 mb-2">
          OTP has been sent to your phone: <strong>{{ maskedPhone }}</strong>
        </p>
        <label class="mb-1 text-sm">Enter OTP</label>
        <input
            v-model="otp"
            type="text"
            maxlength="6"
            class="w-full px-4 py-3 border border-gray-300 rounded-md mb-3 focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="6-digit code"
        />

        <button
            class="bg-primary text-white font-semibold py-3 rounded-md transition-colors flex justify-center items-center disabled:opacity-50 mb-2"
            type="submit"
            :disabled="loading"
        >
          <span v-if="!loading">VERIFY OTP</span>
          <svg v-else class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
          </svg>
        </button>

        <!-- Resend OTP Button -->
        <button
            type="button"
            class="bg-gray-200 text-gray-700 py-2 rounded-md mb-3 disabled:opacity-50"
            :disabled="resendDisabled"
            @click="resendOtp"
        >
          Resend OTP <span v-if="resendDisabled">({{ countdown }}s)</span>
        </button>

        <!-- پیام خطا/موفقیت -->
        <p v-if="errorMessage" class="mt-3 text-red-600 text-sm">{{ errorMessage }}</p>
        <p v-if="successMessage" class="mt-3 text-green-600 text-sm">{{ successMessage }}</p>
      </form>

      <!-- Footer -->
      <div class="flex flex-col items-center mt-10 text-sm text-[#4D2F00]">
        <component :is="LockKeyhole"/>
        Always use your Homebanking with safety
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue"
import { Eye, EyeOff, LockKeyhole } from 'lucide-vue-next'
import api from '@/services/api'
import { useRouter } from 'vue-router'
import {startAuthWatcher} from "@/utils/authWatcher";

const user = ref("")
const currentPassword = ref("")
const showCurrentPassword = ref(false)

const otp = ref("")
const showOtpBox = ref(false)
const userId = ref(null)
const loading = ref(false)

const errorMessage = ref("")   // پیام خطا
const successMessage = ref("") // پیام موفقیت

// Resend OTP timer
const countdown = ref(60)
const resendDisabled = ref(true)
let timer = null
const maskedPhone = ref("")

const router = useRouter()

onMounted(() => {
  const token = localStorage.getItem('token')
  if (token) router.push('/')
})

// Start countdown for Resend OTP
function startCountdown() {
  resendDisabled.value = true
  countdown.value = 60
  timer = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      clearInterval(timer)
      resendDisabled.value = false
    }
  }, 1000)
}

async function handleSubmit() {
  errorMessage.value = ""
  successMessage.value = ""
  try {
    loading.value = true
    const res = await api.post('/login', {
      email: user.value,
      password: currentPassword.value,
    })
    maskedPhone.value = res.data.masked_phone || ""
    successMessage.value = "OTP sent to your phone"
    showOtpBox.value = true
    userId.value = res.data.user_id
    startCountdown() // شروع تایمر بعد از login
  } catch (err) {
    errorMessage.value = err.response?.data?.message || "Login failed"
  } finally {
    loading.value = false
  }
}

async function handleOtpSubmit() {
  errorMessage.value = ""
  successMessage.value = ""
  try {
    loading.value = true
    const res = await api.post('/verify-otp', {
      user_id: userId.value,
      otp: otp.value
    })
    localStorage.setItem('token', res.data.token)
    localStorage.setItem('user', JSON.stringify(res.data.user))
    localStorage.setItem('utc', JSON.stringify(res.data.utc_time))
    localStorage.setItem('force_password_change', res.data.force_password_change)
    localStorage.setItem('user_status', res.data.user.status)
    localStorage.setItem('restrict_reason', res.data.user.restrict_reason)
    localStorage.setItem('token_timestamp', Date.now())

    successMessage.value = "Login successful! Welcome " + res.data.user.name

    startAuthWatcher()

    if (res.data.force_password_change) {
      router.push('/change-password')
    } else {
      router.push('/')
    }



  } catch (err) {
    errorMessage.value = err.response?.data?.message || "OTP verification failed"
  } finally {
    loading.value = false
  }
}

// Resend OTP function
async function resendOtp() {
  if (resendDisabled.value) return
  try {
    loading.value = true
    errorMessage.value = ''
    successMessage.value = ''
    await api.post('/resend-otp', { user_id: userId.value })
    successMessage.value = 'OTP resent successfully!'
    startCountdown() // تایمر دوباره شروع شود
  } catch (err) {
    errorMessage.value = err.response?.data?.message || 'Failed to resend OTP'
  } finally {
    loading.value = false
  }
}
</script>

